/**
 * Brand — the top-bar home button (Claude Design `.brand`).
 *
 * A 22px red circular mark housing a pulsing white live-dot, followed by the
 * letter-spaced "HOTLINE" wordmark. The mark glows in the brand (primary) color.
 *
 * NOTE: the visible wordmark is rendered as the string "Hotline" and uppercased
 * purely via CSS (`uppercase`). The accessible/DOM text therefore stays
 * "Hotline", which the Layout test asserts, while it *displays* as "HOTLINE".
 */
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { cn } from './ui/index.js';

export interface BrandProps {
  /** Destination for the home link. */
  to: string;
  className?: string;
}

export function Brand({ to, className }: BrandProps): ReactElement {
  return (
    <Link
      to={to}
      aria-label="Hotline home"
      className={cn(
        'flex items-center gap-[9px] pr-1.5',
        'rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'grid h-[22px] w-[22px] flex-shrink-0 place-items-center rounded-full bg-primary',
          'shadow-[0_0_14px_-2px_var(--hl-primary)]',
        )}
      >
        <span className="h-[7px] w-[7px] rounded-full bg-white motion-safe:animate-pulse" />
      </span>
      <span className="text-[13px] font-extrabold uppercase tracking-[0.19em] text-text">
        Hotline
      </span>
    </Link>
  );
}
